import React from "react";
import { HelpCircle } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@capacit-com/lara-law-shared-ui";
import type { ConsolidationProgressDisplay } from "@/lib/consolidationProgressDisplay";
import {
  AMENDMENT_DOCUMENT_TYPE_DETAIL,
  BEK_DOCUMENT_TYPE_KINDS,
  BEKENDTGØRELSE_AMENDMENT_DETAIL,
  CONSOLIDATION_DRAFT_LABEL,
  CONSOLIDATION_VERIFIED_LABEL,
  getDocumentTypeLabel,
  LAW_DOCUMENT_TYPE_KINDS,
  TIMELINE_CIRCLE_FILL,
  TIMELINE_COMPARE_RING,
  TIMELINE_DIAMOND_FILL,
  TIMELINE_OUT_OF_ORDER_CIRCLE,
  TIMELINE_OUT_OF_ORDER_LABEL,
  TIMELINE_SELECTION_RING,
} from "./timelineNodeAppearance";
import TimelineLegendShape from "./TimelineLegendShape";

type TimelineLegendProps = {
  anchorConsolidationProgress?: ConsolidationProgressDisplay | null;
  /** BEK timelines only show Bekendtgørelse + Ændringsbekendtgørelse (no hovedlov/LBK/out-of-order). */
  isBekTimeline?: boolean;
};

const LegendRow: React.FC<{
  sample: React.ReactNode;
  label: string;
  detail?: string;
}> = ({ sample, label, detail }) => (
  <div className="flex items-start gap-2.5">
    <div className="flex h-5 w-5 shrink-0 items-center justify-center">
      {sample}
    </div>
    <div className="min-w-0 text-xs leading-snug">
      <span className="font-medium text-foreground">{label}</span>
      {detail && (
        <span className="text-muted-foreground"> {detail}</span>
      )}
    </div>
  </div>
);

const TimelineLegend: React.FC<TimelineLegendProps> = ({
  anchorConsolidationProgress = null,
  isBekTimeline = false,
}) => (
  <HoverCard openDelay={100} closeDelay={80}>
    <HoverCardTrigger asChild>
      <button
        type="button"
        className="absolute right-0 top-1 z-10 flex size-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Vis tidslinje-forklaring"
      >
        <HelpCircle className="size-4" />
      </button>
    </HoverCardTrigger>
    <HoverCardContent
      align="end"
      side="bottom"
      className="w-80 space-y-3 p-3"
    >
      <p className="text-sm font-medium text-foreground">Tidslinje</p>

      <div className="space-y-2">
        {isBekTimeline
          ? BEK_DOCUMENT_TYPE_KINDS.map((kind) => (
              <LegendRow
                key={kind}
                sample={
                  <TimelineLegendShape
                    shape="circle"
                    fillClass={TIMELINE_CIRCLE_FILL[kind]}
                  />
                }
                label={getDocumentTypeLabel(kind)}
                detail={
                  kind === "ændringsbekendtgørelse"
                    ? BEKENDTGØRELSE_AMENDMENT_DETAIL
                    : undefined
                }
              />
            ))
          : LAW_DOCUMENT_TYPE_KINDS.map((kind) => (
              <LegendRow
                key={kind}
                sample={
                  <TimelineLegendShape
                    shape="circle"
                    fillClass={TIMELINE_CIRCLE_FILL[kind]}
                  />
                }
                label={getDocumentTypeLabel(kind)}
                detail={
                  kind === "amendment"
                    ? AMENDMENT_DOCUMENT_TYPE_DETAIL
                    : undefined
                }
              />
            ))}
      </div>

      {!isBekTimeline && (
        <div className="space-y-2 border-t pt-2">
          <LegendRow
            sample={
              <TimelineLegendShape
                shape="circle"
                fillClass={TIMELINE_OUT_OF_ORDER_CIRCLE}
              />
            }
            label={TIMELINE_OUT_OF_ORDER_LABEL}
            detail="(Ændringslov placeret uden for kronologisk rækkefølge på tidslinjen)"
          />
        </div>
      )}

      <div className="space-y-2 border-t pt-2">
        <LegendRow
          sample={
            <TimelineLegendShape
              shape="diamond"
              fillClass={TIMELINE_DIAMOND_FILL.APPROVED}
            />
          }
          label={CONSOLIDATION_VERIFIED_LABEL}
        />
        <LegendRow
          sample={
            <TimelineLegendShape
              shape="diamond"
              fillClass={TIMELINE_DIAMOND_FILL.NEED_REVIEW}
            />
          }
          label={CONSOLIDATION_DRAFT_LABEL}
        />
      </div>

      <div className="space-y-2 border-t pt-2">
        <LegendRow
          sample={
            <TimelineLegendShape
              shape="circle"
              fillClass={
                isBekTimeline
                  ? TIMELINE_CIRCLE_FILL.bekendtgørelse
                  : TIMELINE_CIRCLE_FILL.hovedlov
              }
              ringClass={TIMELINE_SELECTION_RING}
            />
          }
          label="Nuværende lovtekst"
        />
        <LegendRow
          sample={
            <TimelineLegendShape
              shape="diamond"
              fillClass={TIMELINE_DIAMOND_FILL.APPROVED}
              ringClass={TIMELINE_SELECTION_RING}
            />
          }
          label="Nuværende konsolidering"
        />
        <LegendRow
          sample={
            <TimelineLegendShape
              shape="circle"
              fillClass={TIMELINE_CIRCLE_FILL.amendment}
              ringClass={TIMELINE_COMPARE_RING}
            />
          }
          label="Sammenligningsmål"
        />
      </div>

      {anchorConsolidationProgress && (
        <p className="border-t pt-2 text-xs text-muted-foreground">
          {anchorConsolidationProgress.label}
        </p>
      )}
    </HoverCardContent>
  </HoverCard>
);

export default TimelineLegend;
